import {
  Coding,
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer
} from '@medplum/fhirtypes'
import { FormInstance } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { createValueCodingAnswer } from 'fhir/QuestionnaireResponse/utils'
import { PatientChartFormState } from 'components/PatientChart/types'
import { THERAPY_TYPES_AND_CODES } from 'utils/staticLists/therapies'
import {
  PATQuestionnaireSections,
  PATScoreMapping,
  SECTION_QUESTION_FIELDS_MAP,
  SFValuesToUpdateType,
  skilledTaskTitleMap,
  UNANSWERED_DECIMAL_DEFAULT,
  UNANSWERED_SUMMARY_INPUT_DEFAULT
} from './constants'
import { SectionDependentItems } from './types/questionnaire'
import { SECTION_DEPENDENT_ANSWER_LIST_FILTERS } from './constants'
import { PatientBackgroundFormField } from './sections/PatientBackground/formField'
import { ScoreAndSummaryFormField } from './sections/ScoreAndSummary/formField'
import { filterKeysWithSubstrings, formatDiagnosesList } from '../helpers'

const parseFormValueCode = (formValue: unknown): string | null => {
  if (!formValue) return null

  if (typeof formValue === 'object' && 'code' in formValue) {
    return (formValue as { code: string }).code
  }

  if (typeof formValue === 'string' || typeof formValue === 'number') {
    return String(formValue)
  }

  return null
}

type ScoreRangeTable = Array<[number, number] | [number, number, number | string]>

export const evaluateScore = (score: number, rangeTable: ScoreRangeTable): string | null => {
  for (const [minScore, maxScore, result] of rangeTable) {
    if (score >= minScore && score <= maxScore) {
      return `${result}`
    }
  }
  return null
}

type DurationMapping = Array<[number, number]>

export const calculateDuration = (points: number, mappings: DurationMapping): number => {
  let highestDuration = 0
  for (const [minPoints, duration] of mappings) {
    if (points >= minPoints && duration > highestDuration) {
      highestDuration = duration
    }
  }
  return highestDuration
}

export const formatHoursMinutes = (inputHours: number, shortened = false): string | undefined => {
  const hours = Math.floor(inputHours)
  const minutes = Math.round((inputHours - hours) * 60)

  if (hours >= 0 && minutes >= 0) {
    if (shortened) {
      return dayjs().hour(hours).minute(minutes).format('H [hrs] m [min]')
    }
    return dayjs().hour(hours).minute(minutes).format('H [hours and] m [minutes]')
  }

  return
}

export const generateDurationString = (
  score: number,
  sectionSummaryId: string,
  shortened = false
): string | undefined => {
  const scoreMapValues = PATScoreMapping[sectionSummaryId]

  if (score < 0) return

  if (
    sectionSummaryId === PATQuestionnaireSections.ModifierA ||
    sectionSummaryId === PATQuestionnaireSections.ModifierB
  ) {
    const duration = calculateDuration(score, scoreMapValues)

    return shortened ? `${duration} min` : `${duration} minutes`
  } else if (sectionSummaryId === PATQuestionnaireSections.CNASkilledTasks) {
    const duration = calculateDuration(score, scoreMapValues)

    return shortened ? `${duration} hr` : duration === 1 ? `${duration} hour` : `${duration} hours`
  } else if (sectionSummaryId === 'total') {
    return formatHoursMinutes(score, shortened)
  }
}

export const generateScoreString = (
  score: number,
  sectionSummaryId: string
): string | undefined => {
  if (
    sectionSummaryId === PATQuestionnaireSections.ModifierA ||
    sectionSummaryId === PATQuestionnaireSections.ModifierB ||
    sectionSummaryId === PATQuestionnaireSections.CNASkilledTasks ||
    sectionSummaryId === PATQuestionnaireSections.BradenScale
  ) {
    return score === 1 ? `${score} point` : `${score} points`
  }

  return
}

export const generateScoreAndDurationString = (score: number, sectionSummaryId: string): string => {
  const defaultString = UNANSWERED_SUMMARY_INPUT_DEFAULT

  const durationString = generateDurationString(score, sectionSummaryId)

  if (
    sectionSummaryId === PATQuestionnaireSections.ModifierA ||
    sectionSummaryId === PATQuestionnaireSections.ModifierB ||
    sectionSummaryId === PATQuestionnaireSections.CNASkilledTasks
  ) {
    return durationString
      ? `${generateScoreString(score, sectionSummaryId)}, ${durationString}`
      : defaultString
  } else if (sectionSummaryId === 'total') {
    return durationString ?? defaultString
  }

  return defaultString
}

export const determineQualificationStatus = (
  totalDurationValue: QuestionnaireResponseItemAnswer | undefined
): QuestionnaireItemAnswerOption | null => {
  const totalDuration = totalDurationValue?.valueDecimal
  if (totalDuration === undefined || totalDuration === -1) {
    return null
  }

  const qualifyingDuration = 2.5

  const doesQualify = totalDuration >= qualifyingDuration

  return doesQualify
    ? { valueCoding: { code: '0', display: 'Qualified' } }
    : { valueCoding: { code: '1', display: 'Unqualified PAT' } }
}

export const calculateTotalTime = (
  data: Record<string, QuestionnaireResponseItemAnswer>
): number => {
  let totalMinutes = 0

  const defaultValue = -1

  if (Object.keys(data).length < 1) {
    return defaultValue
  }

  // check if any sections are not completed. If so, return default value
  for (const key in data) {
    const targetSection = key.split('/')[1]
    if (targetSection !== 'total') {
      const valueDecimal = data[key]?.valueDecimal
      if (valueDecimal === undefined || valueDecimal === -1) {
        return defaultValue
      }

      const durationString = generateScoreAndDurationString(valueDecimal, targetSection)
      const [, , quantity, unit] = durationString.split(/(?:\s+)/)

      const numericQuantity = parseInt(quantity)

      if (!isNaN(numericQuantity)) {
        if (unit.includes('minute')) {
          totalMinutes += numericQuantity
        } else if (unit.includes('hour')) {
          totalMinutes += numericQuantity * 60
        }
      }
    }
  }

  const totalHours = totalMinutes / 60

  return totalHours
}

/**
 * Calculate total assessment value from form values
 * Takes form values for modifier A, modifier B, and CNA skilled tasks
 * and calculates the total hours
 */
export const calculateTotalAssessmentValue = (formValues: Record<string, unknown>): number => {
  const targetAnswerList: Record<string, QuestionnaireResponseItemAnswer> = {}

  const modifierAValue = formValues[ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A]
  const modifierBValue = formValues[ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B]
  const cnaSkilledTasksValue =
    formValues[ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS]

  if (typeof modifierAValue === 'number' && modifierAValue !== UNANSWERED_DECIMAL_DEFAULT) {
    targetAnswerList[SectionDependentItems.MODIFIER_A] = {
      valueDecimal: modifierAValue
    }
  }

  if (typeof modifierBValue === 'number' && modifierBValue !== UNANSWERED_DECIMAL_DEFAULT) {
    targetAnswerList[SectionDependentItems.MODIFIER_B] = {
      valueDecimal: modifierBValue
    }
  }

  if (
    typeof cnaSkilledTasksValue === 'number' &&
    cnaSkilledTasksValue !== UNANSWERED_DECIMAL_DEFAULT
  ) {
    targetAnswerList[SectionDependentItems.CNA_SKILLED_TASKS] = {
      valueDecimal: cnaSkilledTasksValue
    }
  }

  return calculateTotalTime(targetAnswerList)
}

const parseAndValidateCode = (code: string | undefined | null): number | null => {
  if (code === undefined || code === null || code === '') {
    return null
  }
  const parsed = parseInt(String(code), 10)
  return isNaN(parsed) ? null : parsed
}

export const calculateSumOfScores = (
  response: QuestionnaireResponse | null,
  sectionId: string,
  formValues?: Record<string, unknown>
): number | null => {
  // PatAssessment uncontrolled sections (Modifier A, Modifier B, CNA Skilled Tasks) use form state when available
  const questionFields = SECTION_QUESTION_FIELDS_MAP[sectionId]
  if (questionFields && formValues) {
    // Filter to only fields that exist in formValues
    const existingFields = questionFields.filter(({ formField }) => formField in formValues)

    // If no fields exist at all, return null
    if (existingFields.length === 0) {
      return null
    }

    // Calculate sum from existing fields and check for invalid values
    let totalSum = 0
    let hasInvalidAnswer = false
    let hasAnyValidAnswer = false

    existingFields.forEach(({ formField }) => {
      const formValue = formValues[formField]
      const code = parseFormValueCode(formValue)

      if (code !== null) {
        const parsedValue = parseAndValidateCode(code)
        if (parsedValue !== null) {
          totalSum += parsedValue
          hasAnyValidAnswer = true
        } else {
          hasInvalidAnswer = true
        }
      } else {
        hasInvalidAnswer = true
      }
    })

    // If any existing field has invalid answer, return null
    if (hasInvalidAnswer) {
      return null
    }

    // Check if all fields are present
    const missingFields = questionFields.filter(({ formField }) => !(formField in formValues))

    if (missingFields.length === 0) {
      // All fields present and valid
      return hasAnyValidAnswer ? totalSum : null
    }

    // Some fields are missing
    const requiredFields = new Set<string>()

    if (response) {
      const sectionItem = response.item?.find((section) => section.linkId === sectionId)
      if (sectionItem && sectionItem.item && sectionItem.item.length > 0) {
        questionFields.forEach(({ formField, linkId }) => {
          const fieldExistsInResponse = sectionItem.item?.some(
            (item) =>
              item.linkId === linkId || item.item?.some((subItem) => subItem.linkId === linkId)
          )
          if (fieldExistsInResponse) {
            requiredFields.add(formField)
          }
        })
      } else {
        questionFields.forEach(({ formField }) => {
          requiredFields.add(formField)
        })
      }
    } else {
      questionFields.forEach(({ formField }) => {
        requiredFields.add(formField)
      })
    }

    const allRequiredFieldsPresentAndValid = Array.from(requiredFields).every((formField) => {
      if (!(formField in formValues)) {
        return false // Required field is missing
      }
      const formValue = formValues[formField]
      const code = parseFormValueCode(formValue)
      return code !== null // Required field must have valid value
    })

    if (allRequiredFieldsPresentAndValid && hasAnyValidAnswer && !hasInvalidAnswer) {
      return totalSum
    }

    return null
  }

  // Fallback: read from response (for controlled sections or when formValues not provided)
  if (!response) return null
  const sectionItem = response.item?.find((section) => section.linkId === sectionId)
  let totalSum = 0
  let hasMissingAnswer = false

  sectionItem?.item?.forEach((item) => {
    const answer = item?.item?.[0]?.answer?.[0]?.valueCoding?.code
    const parsedValue = parseAndValidateCode(answer)
    if (parsedValue !== null) {
      totalSum += parsedValue
    } else {
      hasMissingAnswer = true
    }
  })

  return hasMissingAnswer ? null : totalSum
}

export interface LabeledCoding extends Coding {
  label: string
}

export const getScoredValuesWithPositiveCodes = (
  codedData: Record<string, QuestionnaireResponseItemAnswer> | null,
  valueMap: Record<string, string>
): LabeledCoding[] => {
  return Object.entries(codedData ?? []).reduce((acc: LabeledCoding[], [key, value]) => {
    const code = value?.valueCoding?.code ?? ''

    const isPositiveCode = code && parseInt(code) > 0
    const label = valueMap[key]
    if (isPositiveCode && label) {
      acc.push({ ...value.valueCoding, label })
    }
    return acc
  }, [])
}

const hasValueCoding = (
  value: unknown
): value is QuestionnaireResponseItemAnswer & { valueCoding: { display?: string } } => {
  if (typeof value !== 'object' || value === null) return false
  if (!Object.prototype.hasOwnProperty.call(value, 'valueCoding')) return false

  const descriptor = Object.getOwnPropertyDescriptor(value, 'valueCoding')
  if (!descriptor || !('value' in descriptor)) return false
  const valueCoding = descriptor.value
  return typeof valueCoding === 'object' && valueCoding !== null
}

const reduceValueObjectsToPrimitives = (
  value:
    | QuestionnaireResponseItemAnswer
    | Record<string, unknown>
    | string
    | number
    | boolean
    | null
): string => {
  if (hasValueCoding(value)) {
    return value.valueCoding.display ?? ''
  }
  if (typeof value === 'object' && value !== null) {
    const values = Object.values(value)
    if (values.length === 1) {
      const firstValue = values[0]
      return typeof firstValue === 'string' ? firstValue : String(firstValue)
    }
  }
  return typeof value === 'string' ? value : String(value)
}

type TemplateObject = Record<string, { key: string; as?: unknown } | Record<string, unknown>>

type TemplateLeafNode = { key: string; as?: unknown }

const isTemplateLeafNode = (
  value: { key: string; as?: unknown } | Record<string, unknown>
): value is TemplateLeafNode => {
  if (!Object.prototype.hasOwnProperty.call(value, 'key')) return false
  const descriptor = Object.getOwnPropertyDescriptor(value, 'key')
  if (!descriptor || !('value' in descriptor)) return false
  const keyValue = descriptor.value
  return typeof keyValue === 'string'
}

const isTemplateNestedObject = (
  value: { key: string; as?: unknown } | Record<string, unknown>
): value is TemplateObject => {
  return !('key' in value)
}

export const createUpdatedObjectWithAnswers = (
  answers: Record<string, QuestionnaireResponseItemAnswer>,
  templateObject: TemplateObject
): Partial<SFValuesToUpdateType> => {
  const updatedObject: Partial<SFValuesToUpdateType> = {}
  for (const key in templateObject) {
    const templateValue = templateObject[key]
    if (typeof templateValue !== 'object' || templateValue === null) continue

    // Handle nested objects (recursive structure)
    if (isTemplateNestedObject(templateValue)) {
      const nestedObject = createUpdatedObjectWithAnswers(answers, templateValue)
      if (Object.keys(nestedObject).length > 0) {
        updatedObject[key] = nestedObject
      }
      continue
    }

    if (!isTemplateLeafNode(templateValue)) continue
    const { key: valuePath, as: valueType } = templateValue
    const answerFromKey = answers[valuePath]
    if (answerFromKey === undefined) continue

    const normalizedValue = Array.isArray(valueType)
      ? Array.isArray(answerFromKey)
        ? answerFromKey
        : [answerFromKey]
      : answerFromKey

    updatedObject[key] = Array.isArray(normalizedValue)
      ? normalizedValue.map(reduceValueObjectsToPrimitives)
      : reduceValueObjectsToPrimitives(normalizedValue)
  }

  return updatedObject
}

export const validateSFValues = (object: Partial<SFValuesToUpdateType>): string | null => {
  const missingFields: string[] = []

  if (object.patient && typeof object.patient === 'object') {
    if (typeof object.patient.birth_date !== 'string') {
      missingFields.push('patient.birth_date')
    }
    if (typeof object.patient.name !== 'string') {
      missingFields.push('patient.name')
    }
  } else {
    missingFields.push('patient')
  }

  if (typeof object.care_hours !== 'number') {
    missingFields.push('care_hours')
  }
  if (typeof object.pat_status !== 'string') {
    missingFields.push('pat_status')
  }

  // Check if optional properties exist and are of the correct type
  if (object.docs_needed !== undefined && !Array.isArray(object.docs_needed)) {
    missingFields.push('docs_needed')
  }
  if (object.needs_for_docs !== undefined && !Array.isArray(object.needs_for_docs)) {
    missingFields.push('needs_for_docs')
  }

  if (missingFields.length === 0) {
    return null // Object is valid
  }

  return `Salesforce values are missing for the following fields: ${missingFields.join(', ')}`
}

interface CodedAnswerProps {
  sectionReferenceId: string
  questionInitialSelected?: QuestionnaireItemAnswerOption
  questionAnswerOptions?: QuestionnaireItemAnswerOption[]
  sumOfScores: number | null
}

export const getCodedAnswer = ({
  sectionReferenceId,
  questionInitialSelected,
  questionAnswerOptions,
  sumOfScores
}: CodedAnswerProps): QuestionnaireResponseItemAnswer | null => {
  if (!sectionReferenceId) {
    return null
  }

  // For Braden Scale, when sumOfScores is null, default to "0" (No Risk / score not obtained)
  if (sectionReferenceId === PATQuestionnaireSections.BradenScale && sumOfScores === null) {
    const zeroOption = questionAnswerOptions?.find((option) => option?.valueCoding?.code === '0')
    if (zeroOption?.valueCoding?.code) {
      return createValueCodingAnswer({
        code: zeroOption.valueCoding.code,
        display: zeroOption.valueCoding.display || ''
      })
    }
    // Fallback to initialSelected if "0" option not found
    if (questionInitialSelected?.valueCoding) {
      return createValueCodingAnswer({
        code: questionInitialSelected.valueCoding.code || '',
        display: questionInitialSelected.valueCoding.display || ''
      })
    }
  }

  const scoreResult = sumOfScores
    ? evaluateScore(sumOfScores, PATScoreMapping[sectionReferenceId])
    : questionInitialSelected?.valueCoding?.code
  if (scoreResult) {
    const targetAnswerOption = questionAnswerOptions?.find((option) => {
      if (option?.valueCoding?.code) {
        return parseInt(option?.valueCoding?.code) === parseInt(scoreResult)
      }
    })

    const { valueCoding } = targetAnswerOption ?? {}
    if (valueCoding && valueCoding.code && valueCoding.display) {
      const valueCodingAnswer = createValueCodingAnswer({
        code: valueCoding.code,
        display: valueCoding.display
      })
      return valueCodingAnswer
    }
  }
  return null
}

export const isValidStringValue = (value: string): boolean => {
  // Check if the string length exceeds length limit
  if (value.length > 1048576) {
    return false
  }
  // Check if the string contains non-whitespace content
  if (!/\S/.test(value)) {
    return false
  }
  return true
}

export const isDateString = (dateString: string | Date): boolean => {
  const date = new Date(dateString)
  return !isNaN(date.getTime())
}

/**
 * Normalizes a therapy code from stored format (e.g., "PT") to normalized format (e.g., "physical_therapy_pt")
 * Supports lookup by code (for adapter use) or by label (for UI component use)
 */
export const normalizeTherapyCode = (
  storedCode: string,
  lookupBy: 'code' | 'label' = 'code'
): string => {
  const therapyEntry = Object.entries(THERAPY_TYPES_AND_CODES).find(([, data]) => {
    if (lookupBy === 'code') {
      return data.code.toLowerCase() === storedCode.toLowerCase()
    } else {
      return data.label === storedCode
    }
  })

  if (therapyEntry) {
    const [enumValue, { code: shortCode }] = therapyEntry
    return `${enumValue.replace(/-/g, '_')}_${shortCode.toLowerCase()}`
  }

  return storedCode.toLowerCase()
}

/**
 * Normalizes an equipment code to match the format used in equipment options
 * Converts "Adaptive utensils" → "adaptive_utensils" (lowercase + underscores)
 */
export const normalizeEquipmentCode = (code: string): string => {
  return code
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Save Patient Chart to Assessment
 * Populates Patient Verification fields from patient chart data
 * Returns form values to update, allowing caller to check if any changes were made
 */
export const savePatientChartToAssessmentUncontrolled = (
  form: FormInstance,
  patientChartData: PatientChartFormState
): Record<string, string | string[] | Dayjs | undefined> => {
  const { demographics, insuranceInformation } = patientChartData
  const formUpdates: Record<string, string | string[] | Dayjs | undefined> = {}

  if (demographics) {
    const legalName = [demographics.firstName, demographics.lastName].filter(Boolean).join(' ')
    formUpdates[PatientBackgroundFormField.LEGAL_NAME] = legalName || undefined

    formUpdates[PatientBackgroundFormField.DATE_OF_BIRTH] =
      demographics.dateOfBirth && isDateString(demographics.dateOfBirth)
        ? dayjs(demographics.dateOfBirth)
        : undefined

    if (demographics.address) {
      const fullAddress = [
        demographics.address.streetLine1,
        demographics.address.streetLine2,
        demographics.address.city,
        demographics.address.state,
        demographics.address.postalCode
      ]
        .filter(Boolean)
        .join(', ')
      formUpdates[PatientBackgroundFormField.ADDRESS] = fullAddress || undefined
    } else {
      formUpdates[PatientBackgroundFormField.ADDRESS] = undefined
    }

    formUpdates[PatientBackgroundFormField.GENDER] = demographics.gender
      ? demographics.gender.toLowerCase()
      : undefined

    formUpdates[PatientBackgroundFormField.PREFERRED_NAME] =
      demographics.preferredName && demographics.preferredName.trim() !== ''
        ? demographics.preferredName
        : undefined
  } else {
    formUpdates[PatientBackgroundFormField.LEGAL_NAME] = undefined
    formUpdates[PatientBackgroundFormField.DATE_OF_BIRTH] = undefined
    formUpdates[PatientBackgroundFormField.ADDRESS] = undefined
    formUpdates[PatientBackgroundFormField.GENDER] = undefined
    formUpdates[PatientBackgroundFormField.PREFERRED_NAME] = undefined
  }

  formUpdates[PatientBackgroundFormField.MEDICAID_ID] =
    insuranceInformation?.primaryInsuranceId || undefined

  form.setFieldsValue(formUpdates)
  return formUpdates
}

type QuestionnaireAnswerObject = Record<string, QuestionnaireResponseItemAnswer>

export interface GetSectionDependentQuestionValuesType {
  displayValue?: string | string[] | null
  currentAnswerList: QuestionnaireAnswerObject | string[] | null
  itemValue?: QuestionnaireResponseItemAnswer | null | number
  isSectionForItemComplete: boolean | null
}

export const getSectionDependentQuestionValues = ({
  questionLinkId,
  questionInitialSelected,
  questionAnswerOptions,
  answers,
  response,
  formValues
}: {
  questionLinkId: string
  questionInitialSelected?: QuestionnaireItemAnswerOption
  questionAnswerOptions?: QuestionnaireItem['answerOption']
  answers: Record<string, QuestionnaireResponseItemAnswer>
  response: QuestionnaireResponse | null
  formValues?: Record<string, unknown>
}): GetSectionDependentQuestionValuesType | undefined => {
  if (questionLinkId === SectionDependentItems.BRADEN_SCALE) {
    const filteredAnswers = filterKeysWithSubstrings<QuestionnaireResponseItemAnswer>(
      answers,
      SECTION_DEPENDENT_ANSWER_LIST_FILTERS[questionLinkId]
    )
    const targetAnswerList: GetSectionDependentQuestionValuesType['currentAnswerList'] =
      Object.keys(filteredAnswers).length > 0 ? filteredAnswers : null

    // Use form values if available, otherwise fall back to response
    let sumOfScores: number | null = null
    if (formValues) {
      // Calculate from live form values
      sumOfScores = calculateSumOfScores(response, PATQuestionnaireSections.BradenScale, formValues)
    } else if (response) {
      // Fall back to saved response
      sumOfScores = calculateSumOfScores(response, PATQuestionnaireSections.BradenScale)
    }

    // Always get a coded answer - will default to "0" if sumOfScores is null
    const codedAnswer = getCodedAnswer({
      sectionReferenceId: PATQuestionnaireSections.BradenScale,
      questionInitialSelected,
      questionAnswerOptions,
      sumOfScores
    })

    if (codedAnswer) {
      return {
        currentAnswerList: targetAnswerList,
        itemValue: codedAnswer?.valueCoding ?? null,
        isSectionForItemComplete: sumOfScores !== null
      }
    }
  } else if (
    questionLinkId === SectionDependentItems.MODIFIER_A ||
    questionLinkId === SectionDependentItems.MODIFIER_B ||
    questionLinkId === SectionDependentItems.CNA_SKILLED_TASKS
  ) {
    // Map questionLinkId to section name explicitly
    const sectionNameMap: Record<string, string> = {
      [SectionDependentItems.MODIFIER_A]: PATQuestionnaireSections.ModifierA,
      [SectionDependentItems.MODIFIER_B]: PATQuestionnaireSections.ModifierB,
      [SectionDependentItems.CNA_SKILLED_TASKS]: PATQuestionnaireSections.CNASkilledTasks
    }
    const sectionName = sectionNameMap[questionLinkId]

    if (!formValues) {
      return undefined
    }

    const sumOfScores = calculateSumOfScores(response, sectionName, formValues)
    // Convert form values to answer format for compatibility
    const formAnswers: QuestionnaireAnswerObject = {}

    const questionFields = SECTION_QUESTION_FIELDS_MAP[sectionName]
    if (!questionFields) {
      return undefined
    }

    questionFields.forEach(({ formField, linkId }) => {
      const formValue = formValues[formField]
      const code = parseFormValueCode(formValue)
      if (code !== null) {
        formAnswers[linkId] = {
          valueCoding: {
            code,
            display: (formValue as { display?: string }).display || code
          }
        }
      }
    })

    const allFieldsAnswered =
      sectionName === PATQuestionnaireSections.CNASkilledTasks
        ? (() => {
            // First, check if all fields that exist in formValues have valid values
            const allExistingFieldsValid = questionFields.every(({ formField }) => {
              if (!(formField in formValues)) {
                return true // Field doesn't exist - will check separately if it's required
              }
              const formValue = formValues[formField]
              const code = parseFormValueCode(formValue)
              return code !== null // Field exists - must have valid value
            })

            if (!allExistingFieldsValid) {
              return false // Some existing fields don't have valid values
            }

            // Second, determine which fields are required
            const requiredFields: Set<string> = new Set()

            if (response) {
              const sectionItem = response.item?.find((section) => section.linkId === sectionName)
              if (sectionItem && sectionItem.item && sectionItem.item.length > 0) {
                questionFields.forEach(({ formField, linkId }) => {
                  const fieldExistsInResponse = sectionItem.item?.some(
                    (item) =>
                      item.linkId === linkId ||
                      item.item?.some((subItem) => subItem.linkId === linkId)
                  )
                  if (fieldExistsInResponse) {
                    requiredFields.add(formField)
                  }
                })
              } else {
                questionFields.forEach(({ formField }) => {
                  requiredFields.add(formField)
                })
              }
            } else {
              questionFields.forEach(({ formField }) => {
                requiredFields.add(formField)
              })
            }

            const allRequiredFieldsAnswered = Array.from(requiredFields).every((formField) => {
              if (!(formField in formValues)) {
                return false // Required field is missing
              }
              const formValue = formValues[formField]
              const code = parseFormValueCode(formValue)
              return code !== null // Required field must have valid value
            })

            return allRequiredFieldsAnswered
          })()
        : questionFields.every(({ formField }) => {
            if (!(formField in formValues)) {
              return false // Field missing - not answered
            }
            const formValue = formValues[formField]
            const code = parseFormValueCode(formValue)
            return code !== null // Field exists - must have valid value
          })

    return {
      currentAnswerList: Object.keys(formAnswers).length > 0 ? formAnswers : null,
      itemValue: sumOfScores ?? UNANSWERED_DECIMAL_DEFAULT,
      isSectionForItemComplete: allFieldsAnswered && sumOfScores !== null
    }
  } else if (questionLinkId === SectionDependentItems.SKILLED_NEEDS_LIST) {
    const cnaSkilledTasksSectionName = SectionDependentItems.CNA_SKILLED_TASKS.split('/')[1]

    if (!formValues) return undefined

    // Convert form values to answer format for compatibility
    const formAnswers: QuestionnaireAnswerObject = {}
    const questionFields = SECTION_QUESTION_FIELDS_MAP[cnaSkilledTasksSectionName]
    if (!questionFields) return undefined

    questionFields.forEach(({ formField, linkId }) => {
      const formValue = formValues[formField]
      const code = parseFormValueCode(formValue)
      if (code !== null) {
        formAnswers[linkId] = {
          valueCoding: {
            code,
            display: (formValue as { display?: string }).display || code
          }
        }
      }
    })

    const targetAnswerList: QuestionnaireAnswerObject = formAnswers

    const skilledTaskList =
      getScoredValuesWithPositiveCodes(targetAnswerList, skilledTaskTitleMap) ?? null

    if (!skilledTaskList || skilledTaskList.length === 0) {
      const sumOfScores = calculateSumOfScores(response, cnaSkilledTasksSectionName, formValues)
      return {
        currentAnswerList: [],
        displayValue: [],
        isSectionForItemComplete: sumOfScores !== null
      }
    }

    const skilledTaskLabelValues = skilledTaskList.map((skilledTask) => skilledTask.label)
    const sumOfScores = calculateSumOfScores(response, cnaSkilledTasksSectionName, formValues)

    return {
      currentAnswerList: skilledTaskLabelValues,
      displayValue: skilledTaskLabelValues,
      isSectionForItemComplete: sumOfScores !== null
    }
  } else if (questionLinkId === SectionDependentItems.DIAGNOSES_LIST) {
    const filter1 = filterKeysWithSubstrings<QuestionnaireResponseItemAnswer>(
      answers,
      SECTION_DEPENDENT_ANSWER_LIST_FILTERS[SectionDependentItems.DIAGNOSES_LIST][0]
    )
    const filter2 = filterKeysWithSubstrings<QuestionnaireResponseItemAnswer>(
      answers,
      SECTION_DEPENDENT_ANSWER_LIST_FILTERS[SectionDependentItems.DIAGNOSES_LIST][1]
    )
    const targetAnswerList: QuestionnaireAnswerObject = {
      ...filter1,
      ...filter2
    }
    const diagnosesAnswers = formatDiagnosesList(targetAnswerList) ?? null
    return {
      currentAnswerList: targetAnswerList,
      displayValue: diagnosesAnswers,
      isSectionForItemComplete: null
    }
  } else if (questionLinkId === SectionDependentItems.TOTAL_ASSESSMENT) {
    const filteredAnswers = filterKeysWithSubstrings<QuestionnaireResponseItemAnswer>(
      answers,
      SECTION_DEPENDENT_ANSWER_LIST_FILTERS[questionLinkId]
    )
    const targetAnswerList: QuestionnaireAnswerObject = filteredAnswers

    const totalHours = calculateTotalTime(targetAnswerList)
    return {
      currentAnswerList: targetAnswerList,
      itemValue: totalHours ?? UNANSWERED_DECIMAL_DEFAULT,
      isSectionForItemComplete: null
    }
  } else if (questionLinkId === SectionDependentItems.QUALIFICATION_STATUS) {
    // Use form values if available, otherwise fall back to saved answers
    let totalAssessmentValue: QuestionnaireResponseItemAnswer | undefined

    if (formValues) {
      const totalFormValue = formValues[ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL]

      if (typeof totalFormValue === 'number' && totalFormValue !== UNANSWERED_DECIMAL_DEFAULT) {
        totalAssessmentValue = {
          valueDecimal: totalFormValue
        }
      }
    } else {
      // Fall back to saved answers
      const filteredAnswers = filterKeysWithSubstrings<QuestionnaireResponseItemAnswer>(
        answers,
        SECTION_DEPENDENT_ANSWER_LIST_FILTERS[questionLinkId]
      )
      const targetAnswerList: QuestionnaireAnswerObject = filteredAnswers
      totalAssessmentValue = targetAnswerList[SectionDependentItems.TOTAL_ASSESSMENT]
    }

    const qualificationStatus = formValues
      ? determineQualificationStatus(totalAssessmentValue)?.valueCoding ??
        answers[questionLinkId]?.valueCoding
      : answers[questionLinkId]?.valueCoding ??
        determineQualificationStatus(totalAssessmentValue)?.valueCoding

    const filteredAnswers = filterKeysWithSubstrings<QuestionnaireResponseItemAnswer>(
      answers,
      SECTION_DEPENDENT_ANSWER_LIST_FILTERS[questionLinkId]
    )
    const targetAnswerList: QuestionnaireAnswerObject = filteredAnswers

    return {
      currentAnswerList: targetAnswerList,
      itemValue: qualificationStatus ?? null,
      isSectionForItemComplete: null
    }
  }
}
